import { defineStore } from 'pinia'
import { useConfigStore } from '@/stores/ConfigStore'
import { apiClient } from "@/helpers/api.js"
import { handleApiError } from '@/services/errorHandler'
import { useUserStore } from '@/stores/UserStore'
import { useBatchStore } from '@/stores/BatchStore'
import { isTagsEnabled } from '@/helpers/featureFlags'

import { 
  FORM_OPTIONS, 
  BATCH_TYPES, 
  DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS, 
  DEFAULT_BATCH_INFO, 
  createDefaultBatchInfo,

  ASSORTED,
  BATCH_TYPE_LABEL,
  INVENTORY_BETA_ELIGIBLE_VIA,
  INVENTORY_BETA_SURVEY_TRIGGER,
} from '@/constants'

export const useCreateBatchStore = defineStore('CreateBatchStore', {
  state: () => {
    return {
      formOptions: { ...FORM_OPTIONS },
      batchType: 'sports', 
      batchTypes: [ ...BATCH_TYPES ],
      titleTemplates: [],
      descriptionTemplates: [],
      batchInfo: createDefaultBatchInfo(),
      lockSetsLoader: false,
      selectedLockSets: [],
      selectedLockSet: null,
      lockSets: [],
      setPage: 1,
      totalSetPages: 0
    }
  },
  getters: {
    preparedFormOptions() {
      const batchStore = useBatchStore()
      const configStore = useConfigStore()
      this.batchType = batchStore.selectedBatch?.type || this.batchType

      return Object.entries(this.formOptions).reduce((acc, [k, item]) => {    
        const item_ = { ...item } 

        if (item_.field === 'collx_type_id' && this.batchType && configStore.config) {
          const configSports = configStore.config[this.batchType === 'tcg' ? 'games' : this.batchType]
          const collxType = configSports && configSports.length ? [...configSports] : []
          item_.options = collxType || []
          const hasAssortedObject = item_.options?.find(item_ => !item_.collx_type_id)
          !hasAssortedObject && this.batchType !== 'nonsport' && item_.options.unshift({ name: ASSORTED[this.batchType], collx_type_id: null })
          item_.label = BATCH_TYPE_LABEL[this.batchType]
        
          item_.selected = { name: this.batchType === 'nonsport' ? configStore.config[this.batchType] ? configStore.config[this.batchType][0].name : '' : ASSORTED[this.batchType], collx_type_id: this.batchType === 'nonsport' ? configStore.config[this.batchType] ? configStore.config[this.batchType][0].collx_type_id : null : null }
        } else if (item_.field === 'type') {
          item_.options = BATCH_TYPES
          item_.selected = item_.options.find(item => item.type === this.batchType)
        } else if (item_.field === 'title_template_id' || item_.field === 'description_template_id') {
          item_.options = configStore.config[item_.key]

          if (item_.options) {
            item_.options = [{ id: null, name: 'Default CDP Template' }, ...item_.options]
          }

          const defaultTemplate = configStore.config[item_.key]?.find(item_ => item_.default)

          if (defaultTemplate) {
            item_.selected = defaultTemplate
          }
        } else {
          item_.options = configStore.config[item_.key]
        }

        acc.push(item_)
        return acc
      }, [])
    }
  },
  actions: {
    updateField(field, value) {
      if (field === 'type') {
        this.batchType = value
      }
      this.batchInfo = { ...this.batchInfo, [field]: value }
    },

    clearCreatingBatchInfo() {
      this.formOptions.batchTypes.selected = DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS.batchTypes
      this.formOptions.sportsAndGamesBasedOnType.selected = DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS.sportsAndGamesBasedOnType
      this.formOptions.titleTemplates.selected = DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS.titleTemplates
      this.formOptions.descriptionTemplates.selected = DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS.descriptionTemplates
      this.batchInfo = createDefaultBatchInfo()
      this.titleTemplates = []
      this.descriptionTemplates = []
    },

    async fetchTitleAndDescriptionTemplates() {
      const descriptionTemplates = []
      const titleTemplates = []

      try {
        const { data } = await apiClient().get(`/templates`)

        data.forEach((template) => {
          if (template.type === 'title') {
              titleTemplates.push(template)
          } else if (template.type === 'description') {
              descriptionTemplates.push(template)
          }
        })

        this.titleTemplates = [ { id: null, name: 'Default CDP Template' }, ...titleTemplates ] 
        this.descriptionTemplates = [ { id: null, name: 'Default CDP Template' }, ...descriptionTemplates ]
      } catch (error) {
        handleApiError(error, { component: 'CreateBatchStore', action: 'fetchTitleAndDescriptionTemplates' })
      }
    },

    async createBatch() {
      const userStore = useUserStore()
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      try {
        const {data} = await apiClient().post(`/batches`, {
          name: this.batchInfo.name,
          collx_type_id: this.batchInfo.collx_type_id?.collx_type_id,
          condition: this.batchInfo.condition.name,
          description_template_id: this.batchInfo.description_template_id || this.preparedFormOptions.find(item => item.key === 'descriptionTemplates')?.selected.id || this.formOptions.descriptionTemplates.selected.id ||  null,
          title_template_id: this.batchInfo.title_template_id || this.preparedFormOptions.find(item => item.key === 'titleTemplates')?.selected.id || this.formOptions.titleTemplates.selected.id || null,
          type: this.batchType,
          // XXX FIXME not supported by backend
          purchase_price: this.batchInfo.purchase_price ? parseFloat(this.batchInfo.purchase_price) : null,
          purchase_date: this.batchInfo.purchase_date,
          start_price: this.batchInfo.start_price ? parseFloat(this.batchInfo.start_price) : null,
          cabinet: this.batchInfo.cabinet,
          shelf: this.batchInfo.shelf,
          box: this.batchInfo.box,
          row: this.batchInfo.row,
          section: this.batchInfo.section,
          description: this.batchInfo.description,
          // Only with the `tags` flag on — the field isn't rendered otherwise,
          // so there is nothing to send (see `isTagsEnabled`).
          ...(isTagsEnabled() ? { tag_ids: this.batchInfo.tag_ids } : {}),
          user_id: userStore.user.id, // XXX this should really be pulled from authentication
          new_sku: true,
        })

        if (!data) {
          throw new Error('data not found', data)
        }

        // A dealer with a batch has something for the new Inventory to show —
        // which is what the beta's enrollment waits for. Write-once and
        // deliberately NOT awaited: enrollment bookkeeping must never delay, or
        // fail, the batch the user actually asked for.
        userStore.markInventoryBetaEligible(INVENTORY_BETA_ELIGIBLE_VIA.BATCH_CREATED)

        // …and, for a dealer already IN the beta, this is real work done in it
        // — one of the two things that make them worth surveying.
        // The action decides whether it's actually time; this just reports.
        userStore.markInventoryBetaSurveyEligible(INVENTORY_BETA_SURVEY_TRIGGER.BATCH_CREATED)

        return data ? data.id : null
      } catch (error) {
        handleApiError(error, { component: 'CreateBatchStore', action: 'createBatch' })
        return null
      }
    },

    async fetchLockSets(value, page = 1) {
      const batchStore = useBatchStore()

      try {
        const kind = this.batchInfo?.type ? this.batchInfo?.type : batchStore.selectedBatch?.type ? batchStore.selectedBatch?.type : 'sports'
        const type = this.batchInfo?.collx_type_id?.collx_type_id ? this.batchInfo?.collx_type_id?.collx_type_id : batchStore.selectedBatch?.collx_type_id ? batchStore.selectedBatch?.collx_type_id : null
        
        const query = value || ''
        const params = {
          page,
          q: query,
          kind,
          type
        }

        this.lockSetsLoader = true

        if (page > this.totalPages) return 

        if (query) {
          params.q = query
        }

        const { data } = await apiClient(true).get(`/search/sets`, { params })
        
        if (page > 1) {
          this.lockSets = [...this.lockSets, ...data?.matches]
        } else {
          this.lockSets = data?.matches || []
        }

        this.totalSetPages = data?.totalPages

        this.lockSetsLoader = false
      } catch (error) {
        handleApiError(error, { component: 'CreateBatchStore', action: 'fetchLockSets' })
        this.lockSetsLoader = false
      }
    },

    async createLockedSets(batchId) {
      try {
        this.selectedLockSets = this.selectedLockSets.reduce((acc, item) => {
          if (!item.id) {
            acc.push({
              ...item,
              batch_id: batchId
            })
          }
          return acc
        }, [])

        await apiClient().post(`/locked-sets`, this.selectedLockSets)

      } catch (error) {
        handleApiError(error, { component: 'CreateBatchStore', action: 'createLockedSets' })
      }
    },

    async updateLockedSets(batchId, includeParallels) {
      try {
        await apiClient().put(`/locked-sets/${batchId}`, { include_parallels: includeParallels })
      } catch (error) {
        handleApiError(error, { component: 'CreateBatchStore', action: 'updateLockedSets' })
      }
    },

    async deleteLockedSets(setId) {
      const batchStore = useBatchStore()
      try {
        await apiClient().delete(`/locked-sets/${setId}`)

        batchStore.fetchBatch(batchStore.selectedBatchId)
      } catch (error) {
        handleApiError(error, { component: 'CreateBatchStore', action: 'deleteLockedSets' })
      }
    }
  }
})